import L from "leaflet";

const STATE_KEY = "hydrotools:concert-finder:v1";
const DEFAULT_TM_KEY = String(import.meta.env.VITE_TICKETMASTER_API_KEY || "").trim();

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, Number(n) || 0));
}

function asNum(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function toLocalInputValue(ms) {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

function parseLocalInputMs(v, fallbackMs = null) {
  const ms = new Date(String(v || "")).getTime();
  return Number.isFinite(ms) ? ms : fallbackMs;
}

function startOfDay(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function endOfDay(ms) {
  const d = new Date(ms);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function addDays(ms, days) {
  return ms + (days * 24 * 3600 * 1000);
}

function nextWeekendRange(nowMs = Date.now()) {
  const dow = new Date(nowMs).getDay();
  const daysToSaturday = (6 - dow + 7) % 7;
  const startMs = startOfDay(addDays(nowMs, daysToSaturday));
  return { startMs, endMs: endOfDay(addDays(startMs, 1)) };
}

function currentWeekendRange(nowMs = Date.now()) {
  const dow = new Date(nowMs).getDay();
  if (dow === 6 || dow === 0) {
    const sundayOffset = dow === 6 ? 1 : 0;
    return { startMs: nowMs, endMs: endOfDay(addDays(nowMs, sundayOffset)) };
  }
  return nextWeekendRange(nowMs);
}

function resolveTimeWindow(windowMode, customStartLocal, customEndLocal) {
  const nowMs = Date.now();
  switch (String(windowMode || "any")) {
    case "next3days":
      return { startMs: nowMs, endMs: addDays(nowMs, 3) };
    case "next7days":
      return { startMs: nowMs, endMs: addDays(nowMs, 7) };
    case "thisWeekend":
      return currentWeekendRange(nowMs);
    case "nextWeekend": {
      const current = currentWeekendRange(nowMs);
      const nextStart = startOfDay(addDays(current.startMs, 7));
      return { startMs: nextStart, endMs: endOfDay(addDays(nextStart, 1)) };
    }
    case "custom": {
      const startMs = parseLocalInputMs(customStartLocal, nowMs);
      const endMs = parseLocalInputMs(customEndLocal, addDays(startMs, 3));
      return { startMs, endMs: endMs > startMs ? endMs : addDays(startMs, 1) };
    }
    default:
      return { startMs: null, endMs: null };
  }
}

function formatWindowLabel(windowMode, customStartLocal, customEndLocal) {
  const labels = {
    any: "Any time",
    next3days: "Next 3 days",
    next7days: "Next 7 days",
    thisWeekend: "This coming weekend",
    nextWeekend: "Next weekend",
    custom: "Custom window",
  };
  if (windowMode !== "custom") return labels[windowMode] || labels.any;
  const range = resolveTimeWindow(windowMode, customStartLocal, customEndLocal);
  return `${new Date(range.startMs).toLocaleString()} to ${new Date(range.endMs).toLocaleString()}`;
}

function toTicketmasterDateTime(ms) {
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    return {
      apiKey: typeof x.apiKey === "string" ? x.apiKey : DEFAULT_TM_KEY,
      mode: x.mode === "map" ? "map" : "point",
      lat: asNum(x.lat, 37.7749),
      lon: asNum(x.lon, -122.4194),
      radiusMiles: clamp(asNum(x.radiusMiles, 50), 5, 300),
      keyword: typeof x.keyword === "string" ? x.keyword : "",
      windowMode: ["any", "next3days", "next7days", "thisWeekend", "nextWeekend", "custom"].includes(String(x.windowMode || ""))
        ? String(x.windowMode)
        : "any",
      customStartLocal: typeof x.customStartLocal === "string" ? x.customStartLocal : toLocalInputValue(Date.now()),
      customEndLocal: typeof x.customEndLocal === "string" ? x.customEndLocal : toLocalInputValue(addDays(Date.now(), 3)),
      pickMode: x.pickMode === true,
    };
  } catch {
    return {
      apiKey: DEFAULT_TM_KEY,
      mode: "point",
      lat: 37.7749,
      lon: -122.4194,
      radiusMiles: 50,
      keyword: "",
      windowMode: "any",
      customStartLocal: toLocalInputValue(Date.now()),
      customEndLocal: toLocalInputValue(addDays(Date.now(), 3)),
      pickMode: false,
    };
  }
}

function saveState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

function haversineMiles(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (Number(d) * Math.PI) / 180;
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dp = toRad(lat2 - lat1);
  const dl = toRad(lon2 - lon1);
  const aa = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  const km = 6371 * (2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa)));
  return km * 0.621371;
}

function boundsCoverRadiusMiles(bounds) {
  if (!bounds?.isValid?.()) return 50;
  const c = bounds.getCenter();
  const corners = [
    bounds.getNorthWest(),
    bounds.getNorthEast(),
    bounds.getSouthWest(),
    bounds.getSouthEast(),
  ];
  const radius = Math.max(...corners.map((pt) => haversineMiles(c.lat, c.lng, pt.lat, pt.lng)));
  return clamp(Math.ceil(radius), 5, 300);
}

function pointInBounds(lat, lon, bounds) {
  if (!bounds?.isValid?.()) return true;
  return lat >= bounds.getSouth() && lat <= bounds.getNorth() && lon >= bounds.getWest() && lon <= bounds.getEast();
}

function formatEventTime(row) {
  const date = String(row.localDate || "").trim();
  const time = String(row.localTime || "").trim();
  const tz = String(row.timezone || "").trim();
  if (!date && !time) return "--";
  return `${date || ""}${time ? ` ${time}` : ""}${tz ? ` ${tz}` : ""}`.trim();
}

function normalizeTicketmasterEvent(raw, anchor = null) {
  const venue = raw?._embedded?.venues?.[0] || {};
  const lat = asNum(venue?.location?.latitude ?? raw?.location?.latitude, null);
  const lon = asNum(venue?.location?.longitude ?? raw?.location?.longitude, null);
  return {
    id: String(raw?.id || ""),
    name: String(raw?.name || "Untitled Event"),
    url: String(raw?.url || ""),
    venue: String(venue?.name || ""),
    city: String(venue?.city?.name || ""),
    state: String(venue?.state?.stateCode || venue?.state?.name || ""),
    country: String(venue?.country?.countryCode || ""),
    address: String(venue?.address?.line1 || ""),
    lat,
    lon,
    localDate: String(raw?.dates?.start?.localDate || ""),
    localTime: String(raw?.dates?.start?.localTime || ""),
    timezone: String(raw?.dates?.timezone || venue?.timezone || ""),
    status: String(raw?.dates?.status?.code || ""),
    classification: String(raw?.classifications?.[0]?.segment?.name || ""),
    distanceMiles: anchor && Number.isFinite(lat) && Number.isFinite(lon)
      ? haversineMiles(anchor.lat, anchor.lon, lat, lon)
      : null,
  };
}

async function fetchTicketmasterConcerts({ apiKey, lat, lon, radiusMiles, keyword = "", startDateTime = null, endDateTime = null, size = 100 }) {
  const params = new URLSearchParams({
    apikey: String(apiKey || "").trim(),
    classificationName: "music",
    latlong: `${Number(lat)},${Number(lon)}`,
    radius: String(clamp(radiusMiles, 5, 300)),
    unit: "miles",
    size: String(clamp(size, 1, 200)),
    sort: "date,asc",
    locale: "*",
  });
  if (String(keyword || "").trim()) params.set("keyword", String(keyword).trim());
  if (startDateTime) params.set("startDateTime", String(startDateTime));
  if (endDateTime) params.set("endDateTime", String(endDateTime));
  const res = await fetch(`/api/concert-finder/ticketmaster/events.json?${params.toString()}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = json?.errors?.[0]?.detail || json?.fault?.faultstring || `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return Array.isArray(json?._embedded?.events) ? json._embedded.events : [];
}

export const concertFinderTool = {
  id: "concert-finder",
  name: "Concert Finder",
  init(app) {
    this._rt = this._rt ?? createRuntime(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let state = readState();
  let status = state.apiKey ? "Ready." : "Paste a Ticketmaster API key to search.";
  let loading = false;
  let results = [];
  let selectedId = "";
  let mapClickHandler = null;
  const markerLayer = L.layerGroup();
  const markerById = new Map();

  function selectedRow() {
    return results.find((row) => row.id === selectedId) || null;
  }

  function persist() {
    saveState(state);
  }

  function setPickMode(next) {
    state = { ...state, pickMode: !!next };
    persist();
    render();
  }

  function setPoint(lat, lon) {
    state = {
      ...state,
      lat: clamp(lat, -90, 90),
      lon: clamp(lon, -180, 180),
      mode: "point",
    };
    persist();
  }

  function drawMarkers() {
    markerLayer.clearLayers();
    markerById.clear();
    results.forEach((row) => {
      if (!Number.isFinite(row.lat) || !Number.isFinite(row.lon)) return;
      const selected = row.id === selectedId;
      const marker = L.circleMarker([row.lat, row.lon], {
        radius: selected ? 8 : 6,
        color: selected ? "#f8fafc" : "#0f172a",
        weight: selected ? 2 : 1.3,
        fillColor: selected ? "#22c55e" : "#38bdf8",
        fillOpacity: 0.92,
      })
        .bindTooltip(`${row.name}${row.venue ? ` @ ${row.venue}` : ""}`)
        .on("click", () => {
          selectedId = row.id;
          render();
          drawMarkers();
        })
        .addTo(markerLayer);
      markerById.set(row.id, marker);
    });
  }

  function fitResults() {
    const pts = results.filter((row) => Number.isFinite(row.lat) && Number.isFinite(row.lon)).map((row) => [row.lat, row.lon]);
    if (!pts.length) return;
    const b = L.latLngBounds(pts);
    if (b.isValid()) app.map.instance.fitBounds(b.pad(0.15));
  }

  function bindBottomPanelControls() {
    const root = document.getElementById("concert-bottom-card");
    if (!root) return;
    root.onclick = (e) => {
      const rowId = e.target?.closest?.("[data-concert-row]")?.getAttribute?.("data-concert-row");
      if (rowId) {
        selectedId = rowId;
        const row = selectedRow();
        if (row && Number.isFinite(row.lat) && Number.isFinite(row.lon)) {
          app.map.instance.setView([row.lat, row.lon], Math.max(10, app.map.instance.getZoom()));
        }
        renderBottomPanel();
        drawMarkers();
        return;
      }
      if (e.target?.closest?.("#concert-fit-results")) {
        fitResults();
        return;
      }
      if (e.target?.closest?.("#concert-clear-results")) {
        results = [];
        selectedId = "";
        drawMarkers();
        render();
        renderBottomPanel();
      }
    };
  }

  function renderBottomPanel() {
    const selected = selectedRow();
    const rowsHtml = results.length
      ? results.map((row) => `
        <tr data-concert-row="${esc(row.id)}" style="${row.id === selectedId ? "background:rgba(34,197,94,.12);" : ""}">
          <td style="padding:6px 8px;"><strong>${esc(row.name)}</strong></td>
          <td style="padding:6px 8px;">${esc(row.venue || "--")}</td>
          <td style="padding:6px 8px;">${esc(formatEventTime(row))}</td>
          <td style="padding:6px 8px;">${esc([row.city, row.state].filter(Boolean).join(", ") || "--")}</td>
          <td style="padding:6px 8px;">${Number.isFinite(row.distanceMiles) ? `${row.distanceMiles.toFixed(1)} mi` : "--"}</td>
          <td style="padding:6px 8px;">${row.url ? `<a href="${esc(row.url)}" target="_blank" rel="noreferrer">Open</a>` : "--"}</td>
        </tr>
      `).join("")
      : `<tr><td colspan="6" class="small" style="padding:8px;">No concerts loaded.</td></tr>`;

    const detailHtml = selected ? `
      <div class="small" style="margin-bottom:8px;">
        <strong>${esc(selected.name)}</strong>${selected.venue ? ` · ${esc(selected.venue)}` : ""}
        <div style="margin-top:4px;">${esc(formatEventTime(selected))}</div>
        <div style="margin-top:4px;">${esc([selected.address, selected.city, selected.state, selected.country].filter(Boolean).join(", "))}</div>
      </div>
    ` : "";

    app.ui.mountBottomPanel(`
      <div id="concert-bottom-card" style="padding:10px 10px 0 10px;">
        <div class="card" style="padding:10px;">
          <div class="row" style="justify-content:space-between; align-items:center; gap:8px; margin-bottom:8px;">
            <div>
              <div class="lm-title">Concert Results</div>
              <div class="small">${esc(results.length ? `${results.length} events loaded` : "Search to load concerts")}</div>
            </div>
            <div class="row" style="gap:8px;">
              <button id="concert-fit-results" class="btn" ${results.length ? "" : "disabled"}>Zoom Results</button>
              <button id="concert-clear-results" class="btn" ${results.length ? "" : "disabled"}>Clear</button>
            </div>
          </div>
          ${detailHtml}
          <div style="max-height:260px; overflow:auto; border:1px solid rgba(148,163,184,.18); border-radius:8px;">
            <table style="width:100%; border-collapse:collapse; font-size:12px;">
              <thead style="position:sticky; top:0; background:rgba(15,23,42,.92);">
                <tr>
                  <th style="text-align:left; padding:6px 8px;">Event</th>
                  <th style="text-align:left; padding:6px 8px;">Venue</th>
                  <th style="text-align:left; padding:6px 8px;">Date / Time</th>
                  <th style="text-align:left; padding:6px 8px;">Place</th>
                  <th style="text-align:left; padding:6px 8px;">Distance</th>
                  <th style="text-align:left; padding:6px 8px;">Link</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </div>
        </div>
      </div>
    `, { ownerId: "concert-finder", title: "Concert Finder", onShow: bindBottomPanelControls });
  }

  function render() {
    if (!containerEl) return;
    const envNote = DEFAULT_TM_KEY ? "Using env default if panel field is blank." : "No env key detected.";
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Concert Finder</div>
        <div class="small" style="margin-top:6px;">Search Ticketmaster concerts by picked point/radius or by current map view.</div>

        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="concert-api-key">Ticketmaster API Key</label>
          <input id="concert-api-key" type="password" value="${esc(state.apiKey)}" placeholder="Paste Ticketmaster Discovery API key" />
          <div class="small">${esc(envNote)}</div>
        </div>

        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="concert-mode">Search Area</label>
          <select id="concert-mode">
            <option value="point" ${state.mode === "point" ? "selected" : ""}>Picked Point + Radius</option>
            <option value="map" ${state.mode === "map" ? "selected" : ""}>Current Map View (BBox)</option>
          </select>
        </div>

        <div class="row lm-add-row" style="margin-top:8px; gap:8px; align-items:end;">
          <div style="flex:1;">
            <label class="small" for="concert-lat">Lat</label>
            <input id="concert-lat" type="number" step="0.0001" value="${esc(state.lat)}" />
          </div>
          <div style="flex:1;">
            <label class="small" for="concert-lon">Lon</label>
            <input id="concert-lon" type="number" step="0.0001" value="${esc(state.lon)}" />
          </div>
        </div>

        <div class="row lm-add-row" style="margin-top:8px; gap:8px; align-items:end;">
          <div style="flex:1;">
            <label class="small" for="concert-radius">Radius (mi)</label>
            <input id="concert-radius" type="number" min="5" max="300" step="5" value="${esc(state.radiusMiles)}" />
          </div>
          <div style="flex:1;">
            <label class="small" for="concert-keyword">Keyword</label>
            <input id="concert-keyword" type="text" value="${esc(state.keyword)}" placeholder="Optional artist / venue / genre" />
          </div>
        </div>

        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="concert-window-mode">Time Window</label>
          <select id="concert-window-mode">
            <option value="any" ${state.windowMode === "any" ? "selected" : ""}>Any time</option>
            <option value="next3days" ${state.windowMode === "next3days" ? "selected" : ""}>Next 3 days</option>
            <option value="next7days" ${state.windowMode === "next7days" ? "selected" : ""}>Next 7 days</option>
            <option value="thisWeekend" ${state.windowMode === "thisWeekend" ? "selected" : ""}>This coming weekend</option>
            <option value="nextWeekend" ${state.windowMode === "nextWeekend" ? "selected" : ""}>Next weekend</option>
            <option value="custom" ${state.windowMode === "custom" ? "selected" : ""}>Custom</option>
          </select>
        </div>

        <div id="concert-custom-window" class="row lm-add-row" style="margin-top:8px; gap:8px; align-items:end; ${state.windowMode === "custom" ? "" : "display:none;"}">
          <div style="flex:1;">
            <label class="small" for="concert-window-start">Start</label>
            <input id="concert-window-start" type="datetime-local" value="${esc(state.customStartLocal)}" />
          </div>
          <div style="flex:1;">
            <label class="small" for="concert-window-end">End</label>
            <input id="concert-window-end" type="datetime-local" value="${esc(state.customEndLocal)}" />
          </div>
        </div>

        <div class="row" style="margin-top:10px; gap:8px; flex-wrap:wrap;">
          <button id="concert-pick" class="btn ${state.pickMode ? "primary" : ""}">${state.pickMode ? "Stop Pick" : "Pick Point"}</button>
          <button id="concert-use-map" class="btn">Use Map Center</button>
          <button id="concert-search" class="btn primary" ${loading ? "disabled" : ""}>${loading ? "Searching..." : "Search Concerts"}</button>
        </div>

        <div class="small" style="margin-top:10px;"><strong>Status:</strong> ${esc(status)}</div>
      </div>
    `;
  }

  function syncFromInputs() {
    if (!containerEl) return;
    state = {
      ...state,
      apiKey: String(containerEl.querySelector("#concert-api-key")?.value || "").trim(),
      mode: String(containerEl.querySelector("#concert-mode")?.value || "point") === "map" ? "map" : "point",
      lat: clamp(asNum(containerEl.querySelector("#concert-lat")?.value, state.lat), -90, 90),
      lon: clamp(asNum(containerEl.querySelector("#concert-lon")?.value, state.lon), -180, 180),
      radiusMiles: clamp(asNum(containerEl.querySelector("#concert-radius")?.value, state.radiusMiles), 5, 300),
      keyword: String(containerEl.querySelector("#concert-keyword")?.value || ""),
      windowMode: String(containerEl.querySelector("#concert-window-mode")?.value || "any"),
      customStartLocal: String(containerEl.querySelector("#concert-window-start")?.value || state.customStartLocal),
      customEndLocal: String(containerEl.querySelector("#concert-window-end")?.value || state.customEndLocal),
    };
    persist();
  }

  async function runSearch() {
    syncFromInputs();
    const apiKey = String(state.apiKey || DEFAULT_TM_KEY || "").trim();
    if (!apiKey) {
      status = "Ticketmaster API key required.";
      render();
      return;
    }
    const bounds = app.map.instance.getBounds();
    const center = state.mode === "map" ? bounds.getCenter() : L.latLng(state.lat, state.lon);
    const radiusMiles = state.mode === "map" ? boundsCoverRadiusMiles(bounds) : state.radiusMiles;
    const windowRange = resolveTimeWindow(state.windowMode, state.customStartLocal, state.customEndLocal);
    loading = true;
    status = state.mode === "map"
      ? `Searching concerts in current map view using a ${radiusMiles} mi cover radius. ${formatWindowLabel(state.windowMode, state.customStartLocal, state.customEndLocal)}.`
      : `Searching concerts within ${radiusMiles} mi of ${center.lat.toFixed(3)}, ${center.lng.toFixed(3)}. ${formatWindowLabel(state.windowMode, state.customStartLocal, state.customEndLocal)}.`;
    render();
    try {
      const rawEvents = await fetchTicketmasterConcerts({
        apiKey,
        lat: center.lat,
        lon: center.lng,
        radiusMiles,
        keyword: state.keyword,
        startDateTime: windowRange.startMs ? toTicketmasterDateTime(windowRange.startMs) : null,
        endDateTime: windowRange.endMs ? toTicketmasterDateTime(windowRange.endMs) : null,
      });
      const anchor = { lat: center.lat, lon: center.lng };
      let rows = rawEvents.map((row) => normalizeTicketmasterEvent(row, anchor));
      if (state.mode === "map") {
        rows = rows.filter((row) => !Number.isFinite(row.lat) || !Number.isFinite(row.lon) || pointInBounds(row.lat, row.lon, bounds));
      }
      rows.sort((a, b) => {
        const ta = new Date(`${a.localDate || ""}T${a.localTime || "00:00:00"}`).getTime();
        const tb = new Date(`${b.localDate || ""}T${b.localTime || "00:00:00"}`).getTime();
        return (Number.isFinite(ta) ? ta : Number.MAX_SAFE_INTEGER) - (Number.isFinite(tb) ? tb : Number.MAX_SAFE_INTEGER);
      });
      results = rows;
      selectedId = rows[0]?.id || "";
      drawMarkers();
      renderBottomPanel();
      status = rows.length
        ? `Loaded ${rows.length} concerts${state.mode === "map" ? " intersecting the current map view" : ""}.`
        : "No concerts returned for the current search.";
    } catch (err) {
      console.error("[ConcertFinder] search failed", err);
      status = `Concert search failed: ${err?.message || String(err)}`;
    } finally {
      loading = false;
      render();
    }
  }

  function handleMapClick(e) {
    if (!state.pickMode) return;
    setPoint(e.latlng.lat, e.latlng.lng);
    status = `Picked point ${e.latlng.lat.toFixed(4)}, ${e.latlng.lng.toFixed(4)}.`;
    render();
  }

  function mount(el) {
    containerEl = el;
    markerLayer.addTo(app.map.instance);
    app.ui.mountBottomPanel('<div class="small">Concert results will appear here.</div>', { ownerId: "concert-finder", title: "Concert Finder" });
    if (results.length) {
      drawMarkers();
      renderBottomPanel();
    } else {
      drawMarkers();
    }
    render();
    mapClickHandler = handleMapClick;
    app.map.instance.on("click", mapClickHandler);
    containerEl.onclick = (e) => {
      if (e.target?.closest?.("#concert-pick")) {
        syncFromInputs();
        setPickMode(!state.pickMode);
        return;
      }
      if (e.target?.closest?.("#concert-use-map")) {
        const c = app.map.instance.getCenter();
        setPoint(c.lat, c.lng);
        status = `Using map center ${c.lat.toFixed(4)}, ${c.lng.toFixed(4)}.`;
        render();
        return;
      }
      if (e.target?.closest?.("#concert-search")) {
        runSearch();
      }
    };
    containerEl.onchange = () => {
      syncFromInputs();
      render();
    };
    containerEl.oninput = () => {
      syncFromInputs();
    };
  }

  function destroy() {
    if (mapClickHandler) {
      try { app.map.instance.off("click", mapClickHandler); } catch {}
      mapClickHandler = null;
    }
    markerLayer.clearLayers();
    try { app.map.instance.removeLayer(markerLayer); } catch {}
    app.ui.unmountBottomPanel("concert-finder");
    if (containerEl) {
      containerEl.onclick = null;
      containerEl.onchange = null;
      containerEl.oninput = null;
    }
    containerEl = null;
  }

  return { mount, destroy };
}
